export interface RoomBoundary {
  x: number;
  y: number;
  width: number;
  height: number;
  tiles: Set<string>; // Set of "x,y" coordinates
  outlineTiles: Set<string>; // Set of "x,y" coordinates for outline
}

export interface RoomOutlineData {
  roomId: string;
  boundary: RoomBoundary;
  color: string;
  lastUpdated: number;
}

export class RoomOutlineService {
  private roomOutlines: Map<string, RoomOutlineData> = new Map();
  private readonly OUTLINE_THICKNESS = 6; // 6-pixel outline thickness
  private readonly TILE_SIZE = 32; // Assuming 32x32 tiles

  /**
   * Add or update a room outline
   */
  addRoomOutline(roomId: string, tiles: Array<{ x: number; y: number }>, color: string): RoomOutlineData {
    const boundary = this.calculateBoundary(tiles);
    const outlineTiles = this.calculateOutlineTiles(boundary, tiles);
    
    const roomOutline: RoomOutlineData = {
      roomId,
      boundary: {
        ...boundary,
        tiles: new Set(tiles.map(t => `${t.x},${t.y}`)),
        outlineTiles
      },
      color,
      lastUpdated: Date.now()
    };

    this.roomOutlines.set(roomId, roomOutline);
    console.log(`[RoomOutlineService] Added/updated room outline for ${roomId}:`, roomOutline);
    
    return roomOutline;
  }

  /**
   * Merge room outlines when they become adjacent
   */
  mergeAdjacentRooms(roomId1: string, roomId2: string): string | null {
    const room1 = this.roomOutlines.get(roomId1);
    const room2 = this.roomOutlines.get(roomId2);

    if (!room1 || !room2) {
      console.warn('[RoomOutlineService] Cannot merge rooms - one or both not found');
      return null;
    }

    // Check if rooms are adjacent
    if (!this.areRoomsAdjacent(room1.boundary, room2.boundary)) {
      console.log('[RoomOutlineService] Rooms are not adjacent, cannot merge');
      return null;
    }

    // Merge tiles
    const mergedTiles = new Set([...room1.boundary.tiles, ...room2.boundary.tiles]);
    const mergedTileArray = Array.from(mergedTiles).map(coord => {
      const [x, y] = coord.split(',').map(Number);
      return { x, y };
    });

    // Use room1's color for the merged room
    const mergedRoom = this.addRoomOutline(roomId1, mergedTileArray, room1.color);
    
    // Remove the second room
    this.roomOutlines.delete(roomId2);

    console.log(`[RoomOutlineService] Merged rooms ${roomId1} and ${roomId2} into ${roomId1}`);
    return roomId1;
  }

  /**
   * Check if two room boundaries are adjacent
   */
  private areRoomsAdjacent(boundary1: RoomBoundary, boundary2: RoomBoundary): boolean {
    // Check if boundaries overlap or are adjacent
    const horizontalAdjacent = boundary1.x + boundary1.width + 1 >= boundary2.x && 
                              boundary2.x + boundary2.width + 1 >= boundary1.x;
    
    const verticalAdjacent = boundary1.y + boundary1.height + 1 >= boundary2.y && 
                            boundary2.y + boundary2.height + 1 >= boundary1.y;

    return horizontalAdjacent && verticalAdjacent;
  }

  /**
   * Calculate the boundary rectangle for a set of tiles
   */
  private calculateBoundary(tiles: Array<{ x: number; y: number }>): Omit<RoomBoundary, 'tiles' | 'outlineTiles'> {
    if (tiles.length === 0) {
      return { x: 0, y: 0, width: 0, height: 0 };
    }

    const minX = Math.min(...tiles.map(t => t.x));
    const maxX = Math.max(...tiles.map(t => t.x));
    const minY = Math.min(...tiles.map(t => t.y));
    const maxY = Math.max(...tiles.map(t => t.y));

    return {
      x: minX,
      y: minY,
      width: maxX - minX + 1,
      height: maxY - minY + 1
    };
  }

  /**
   * Calculate outline tiles around the room boundary
   */
  private calculateOutlineTiles(boundary: Omit<RoomBoundary, 'tiles' | 'outlineTiles'>, tiles: Array<{ x: number; y: number }>): Set<string> {
    const outlineTiles = new Set<string>();
    const tileSet = new Set(tiles.map(t => `${t.x},${t.y}`));

    // Calculate outline area (boundary + outline thickness)
    const outlineStartX = boundary.x - this.OUTLINE_THICKNESS;
    const outlineEndX = boundary.x + boundary.width + this.OUTLINE_THICKNESS;
    const outlineStartY = boundary.y - this.OUTLINE_THICKNESS;
    const outlineEndY = boundary.y + boundary.height + this.OUTLINE_THICKNESS;

    // Generate outline tiles
    for (let x = outlineStartX; x <= outlineEndX; x++) {
      for (let y = outlineStartY; y <= outlineEndY; y++) {
        const coord = `${x},${y}`;
        
        // Skip if it's part of the room tiles
        if (tileSet.has(coord)) {
          continue;
        }

        // Check if this tile is within the outline area
        if (this.isInOutlineArea(x, y, boundary)) {
          outlineTiles.add(coord);
        }
      }
    }

    return outlineTiles;
  }

  /**
   * Check if a tile is within the outline area
   */
  private isInOutlineArea(x: number, y: number, boundary: Omit<RoomBoundary, 'tiles' | 'outlineTiles'>): boolean {
    // Check if tile is within the outline thickness from the boundary
    const distanceFromBoundary = this.getDistanceFromBoundary(x, y, boundary);
    return distanceFromBoundary <= this.OUTLINE_THICKNESS && distanceFromBoundary > 0;
  }

  /**
   * Calculate the minimum distance from a tile to the room boundary
   */
  private getDistanceFromBoundary(x: number, y: number, boundary: Omit<RoomBoundary, 'tiles' | 'outlineTiles'>): number {
    const left = boundary.x;
    const right = boundary.x + boundary.width - 1;
    const top = boundary.y;
    const bottom = boundary.y + boundary.height - 1;

    // If tile is inside the boundary, return 0
    if (x >= left && x <= right && y >= top && y <= bottom) {
      return 0;
    }

    // Calculate minimum distance to boundary edges
    const distToLeft = Math.abs(x - left);
    const distToRight = Math.abs(x - right);
    const distToTop = Math.abs(y - top);
    const distToBottom = Math.abs(y - bottom);

    // Find minimum distance to any boundary edge
    return Math.min(distToLeft, distToRight, distToTop, distToBottom);
  }

  /**
   * Get all outline tiles for a specific room
   */
  getRoomOutline(roomId: string): RoomOutlineData | null {
    return this.roomOutlines.get(roomId) || null;
  }

  /**
   * Get all room outlines
   */
  getAllRoomOutlines(): RoomOutlineData[] {
    return Array.from(this.roomOutlines.values());
  }

  /**
   * Get all room tiles for rendering preserved borders
   */
  getAllRoomTiles(): Array<{ roomId: string; tiles: Array<{ x: number; y: number }>; color: string }> {
    return Array.from(this.roomOutlines.values()).map(roomOutline => ({
      roomId: roomOutline.roomId,
      tiles: Array.from(roomOutline.boundary.tiles).map(coord => {
        const [x, y] = coord.split(',').map(Number);
        return { x, y };
      }),
      color: roomOutline.color
    }));
  }

  /**
   * Remove a room outline
   */
  removeRoomOutline(roomId: string): boolean {
    const removed = this.roomOutlines.delete(roomId);
    if (removed) {
      console.log(`[RoomOutlineService] Removed room outline for ${roomId}`);
    }
    return removed;
  }

  /**
   * Check if a tile is part of any room outline
   */
  isTileInOutline(x: number, y: number): { roomId: string; isOutline: boolean } | null {
    const coord = `${x},${y}`;
    
    for (const [roomId, roomData] of this.roomOutlines) {
      if (roomData.boundary.tiles.has(coord)) {
        return { roomId, isOutline: false };
      }
      if (roomData.boundary.outlineTiles.has(coord)) {
        return { roomId, isOutline: true };
      }
    }
    
    return null;
  }

  /**
   * Get the room that contains a specific tile
   */
  getRoomAtTile(x: number, y: number): string | null {
    const coord = `${x},${y}`;
    
    for (const [roomId, roomData] of this.roomOutlines) {
      if (roomData.boundary.tiles.has(coord)) {
        return roomId;
      }
    }
    
    return null;
  }

  /**
   * Clear all room outlines
   */
  clearAllOutlines(): void {
    this.roomOutlines.clear();
    console.log('[RoomOutlineService] Cleared all room outlines');
  }

  /**
   * Export room outline data for storage
   */
  exportRoomOutlines(): Record<string, any> {
    const exportData: Record<string, any> = {};
    
    for (const [roomId, roomData] of this.roomOutlines) {
      exportData[roomId] = {
        ...roomData,
        boundary: {
          ...roomData.boundary,
          tiles: Array.from(roomData.boundary.tiles),
          outlineTiles: Array.from(roomData.boundary.outlineTiles)
        }
      };
    }
    
    return exportData;
  }

  /**
   * Import room outline data from storage
   */
  importRoomOutlines(data: Record<string, any>): void {
    this.roomOutlines.clear();
    
    for (const [roomId, roomData] of Object.entries(data)) {
      this.roomOutlines.set(roomId, {
        ...roomData,
        boundary: {
          ...roomData.boundary,
          tiles: new Set(roomData.boundary.tiles),
          outlineTiles: new Set(roomData.boundary.outlineTiles)
        }
      });
    }
    
    console.log(`[RoomOutlineService] Imported ${Object.keys(data).length} room outlines`);
  }
} 